import { useContext } from 'react';
import {
  CALIBRATION_MAX_SETS,
  CALIBRATION_STOP_RIR,
  convertEffort,
  effortLabel,
  type CurrentMax,
  type EffortScale,
} from '@heraion/shared';

import { CuePill } from '../primitives/CuePill';
import { calibrationCueOf, type CalibrationCueKind } from './calibrationRamp';
import { LoggerContext } from './loggerContext';
import { rowIdOf, type DraftEntry } from './state/loggerDraftTypes';

// #353: the card's line about a MAX calibration. The offer carries its two
// answers; every other state is the words alone, never only a colour. No
// modal: the athlete answers where the set is. It reads the live logger's
// context, so outside it (the prescribe editor) it renders nothing.
export function CalibrationCue({
  entry,
  effortScale,
}: {
  entry: DraftEntry;
  effortScale: EffortScale;
}) {
  const ctx = useContext(LoggerContext);
  if (!ctx || ctx.mode !== 'log' || entry.calibration === undefined) return null;
  const { deps, draft, dispatch, defaultUnit, theme, t, setSelectedSetIdx } = ctx;
  // "Has a MAX" read as buildPlanStarter reads it: the MAX's value for the slug.
  const liveMaxes: ReadonlyMap<string, CurrentMax> | null = deps.maxesLoaded
    ? deps.maxMeta
    : null;
  const hasMax =
    liveMaxes === null ? null : liveMaxes.get(entry.exerciseId)?.valueKg !== undefined;
  const cue = calibrationCueOf(entry, hasMax);
  if (!cue) return null;
  const offer = cue.kind === 'offer';
  const answer = (accept: boolean) => {
    dispatch({
      type: 'answer_calibration',
      entryIdx: draft.entries.findIndex((e) => rowIdOf(e) === rowIdOf(entry)),
      accept,
      defaultUnit,
    });
    // Accepting replaces the sets with ONE ramp set: a pill selected past it
    // would point at nothing now and at a ramp set later, as the ramp grows.
    if (accept) setSelectedSetIdx(0);
  };
  return (
    <CuePill
      theme={theme}
      tone={offer || cue.kind === 'toCalibrate' ? 'warn' : 'brand'}
      icon={cue.kind === 'done' ? 'check' : 'target'}
      weight="600"
      padY="s2"
      label={calibrationCueText(cue, effortScale, t)}
      live={offer}
      actions={
        offer
          ? [
              {
                label: t('mobile.logger.calibration.accept', { defaultValue: 'Calibrate' }),
                onPress: () => answer(true),
              },
              {
                label: t('mobile.logger.calibration.decline', { defaultValue: 'Not today' }),
                onPress: () => answer(false),
              },
            ]
          : undefined
      }
    />
  );
}

/** The cue's words. The stop is said in the athlete's own effort scale. */
export const calibrationCueText = (
  cue: { kind: CalibrationCueKind; setNumber: number },
  effortScale: EffortScale,
  t: (key: string, opts: Record<string, unknown>) => string,
): string => {
  const stop = convertEffort(CALIBRATION_STOP_RIR, 'rir', effortScale);
  switch (cue.kind) {
    case 'offer':
      return t('mobile.logger.calibration.offer', {
        defaultValue: 'No MAX yet, so this % has no weight. Calibrate it today?',
      });
    case 'toCalibrate':
      return t('mobile.logger.calibration.toCalibrate', {
        defaultValue: 'To calibrate: no MAX yet, so no weight is proposed.',
      });
    case 'first':
      return t('mobile.logger.calibration.first', {
        defaultValue: 'Calibration: sets of 5. Write a light first weight, Metria proposes the rest.',
      });
    case 'ramp':
      return t('mobile.logger.calibration.ramp', {
        n: cue.setNumber,
        count: CALIBRATION_MAX_SETS,
        effort: effortLabel(stop, effortScale),
        defaultValue:
          'Calibration set {{n}}: more weight. It stops at {{effort}} or after {{count}} sets.',
      });
    case 'done':
      return t('mobile.logger.calibration.done', {
        defaultValue: 'Calibration done. Save, and the new MAX waits in Notifications.',
      });
  }
};
